/** @jsxImportSource @emotion/react */
import { useState } from 'react'
import {
  DownloadOutlined,
  LoadingOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import type { MenuProps } from 'antd'
import { t } from '@sportninja/common/i18n'
import {
  ORG_TRANSFER_KINDS,
  isOrgImportBlockedByRegistration,
  orgExportFileName,
  orgExportUrl,
  orgTransferLabel,
  orgTransferTriggerLabel,
  parseOrgTransferKind,
  type OrgTransferDirection,
  type OrgTransferKind,
} from '@sportninja/common/features/competition/operations/utils/orgTeamsTransfer'
import { SkeletonText } from 'src/components/ts-components/SkeletonBlock'
import { useFileDownload } from 'src/hooks/useFileDownload'
import OrgTeamsImportModal from './OrgTeamsImportModal'
import PanelMenuTrigger from './PanelMenuTrigger'
import { panelIconSkeletonStyle } from './panelIconButtonStyle'

const translate = t as (key: string, options?: unknown) => string

const menuItems = (
  direction: OrgTransferDirection,
  isRegistrationOn = false
): MenuProps['items'] =>
  ORG_TRANSFER_KINDS.map((kind) => ({
    key: kind,
    label: orgTransferLabel(direction, kind, translate),
    disabled:
      direction === 'import' &&
      isOrgImportBlockedByRegistration(kind, isRegistrationOn),
  }))

export interface OrgTeamsTransferActionsProps {
  orgId: string
  orgName: string
  /** True while the organization — and so the permission — is still loading. */
  isLoading: boolean
  /** Refreshes what the tab has cached after an import lands. */
  onImported: () => void
  /**
   * The competition's registration flag. While on, Import Teams and Import
   * Player Rosters are disabled — see `isOrgImportBlockedByRegistration`.
   */
  isRegistrationOn: boolean
}

/**
 * Export and Import menus for the organization's teams and rosters — the Org
 * screen's Teams-tab set, on the root competition's panel. Icon-only triggers
 * in the header's icon-button treatment, each opening the same three entries
 * the Org screen lists. Everything is organization-wide: see `orgTeamsTransfer`.
 */
const OrgTeamsTransferActions = ({
  orgId,
  orgName,
  isLoading,
  onImported,
  isRegistrationOn,
}: OrgTeamsTransferActionsProps) => {
  'use memo'

  const [importKind, setImportKind] = useState<OrgTransferKind | null>(null)
  const { download, loading: isExporting } = useFileDownload()

  const closeImport = () => setImportKind(null)

  // While the organization loads its permission reads as denied. A skeleton
  // reserves the footprint so the header does not shift, matching the team
  // panel's roster buttons.
  if (isLoading) {
    return (
      <span
        css={panelIconSkeletonStyle}
        data-testid='org-transfer-skeleton'
        aria-hidden
      >
        <SkeletonText width={34} height={34} />
        <SkeletonText width={34} height={34} />
      </span>
    )
  }

  const exportLabel = orgTransferTriggerLabel('export', translate)
  const importLabel = orgTransferTriggerLabel('import', translate)

  return (
    <>
      <PanelMenuTrigger
        label={exportLabel}
        icon={isExporting ? <LoadingOutlined /> : <DownloadOutlined />}
        disabled={isExporting}
        menu={{
          items: menuItems('export'),
          onClick: ({ key }) => {
            const kind = parseOrgTransferKind(key)
            if (kind) {
              download({
                url: orgExportUrl(orgId, kind),
                fileName: orgExportFileName(orgName, kind),
              })
            }
          },
        }}
      />

      <PanelMenuTrigger
        label={importLabel}
        icon={<UploadOutlined />}
        disabled={false}
        menu={{
          items: menuItems('import', isRegistrationOn),
          onClick: ({ key }) => setImportKind(parseOrgTransferKind(key)),
        }}
      />

      <OrgTeamsImportModal
        kind={importKind}
        orgId={orgId}
        orgName={orgName}
        onClose={closeImport}
        onImported={onImported}
      />
    </>
  )
}

export default OrgTeamsTransferActions
